import React, { useState, useEffect } from 'react';
import {
    Database,
    Send,
    Paperclip,
    ShieldAlert,
    Save,
    Info,
    CheckCircle2,
    AlertCircle,
    HardDrive,
    Clock,
    UserCheck,
    MailCheck,
    FileType,
    XCircle,
    X,
    ZapIcon,
    RefreshCw
} from 'lucide-react';
import adminService from '../../services/adminService';

// MOVED OUTSIDE to prevent re-mounting on every keystroke
const PolicySection = ({ title, icon: Icon, children, description }) => (
    <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200/60 dark:border-slate-800/60 shadow-sm overflow-hidden mb-8 transition-all hover:shadow-md">
        <div className="p-6 border-b border-slate-100 dark:border-slate-800/60 flex items-center justify-between">
            <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-2xl bg-[#4169E1]/10 dark:bg-[#4169E1]/20 flex items-center justify-center text-[#4169E1]">
                    <Icon size={24} />
                </div>
                <div>
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">{title}</h3>
                    <p className="text-xs text-slate-500 font-medium">{description}</p>
                </div>
            </div>
        </div>
        <div className="p-8">
            {children}
        </div>
    </div>
);

const InputField = ({ label, value, onChange, placeholder, type = "text", helper }) => (
    <div className="space-y-2">
        <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">{label}</label>
        <div className="relative group">
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-slate-50 dark:bg-slate-950/50 border border-slate-200 dark:border-slate-800 rounded-2xl px-5 py-3.5 text-sm text-slate-900 dark:text-white outline-none focus:border-[#4169E1] focus:ring-4 focus:ring-[#4169E1]/10 transition-all duration-300"
                placeholder={placeholder}
            />
        </div>
        {helper && <p className="text-[10px] text-slate-400 font-medium">{helper}</p>}
    </div>
);

const PolicyManagement = () => {
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [success, setSuccess] = useState(false);
    const [error, setAppError] = useState('');
    const [forcePropagate, setForcePropagate] = useState(false);

    // Initial state matching user requirements
    const [storagePolicies, setStoragePolicies] = useState({
        defaultQuota: '5 GB',
        maxMailboxSize: '50 GB',
        warningThreshold: '80%',
        attachmentLimit: '25 MB',
        archivePolicy: '180 Days',
        trashRetention: '30 Days'
    });

    const [transmissionPolicies, setTransmissionPolicies] = useState({
        maxRecipients: 100,
        emailsPerHour: 500,
        emailsPerDay: 5000,
        attachmentCount: 10,
        smtpLimit: 20,
        retryAttempts: 5
    });

    const [attachmentPolicies, setAttachmentPolicies] = useState({
        allowedTypes: ['pdf', 'docx', 'xlsx', 'pptx', 'png', 'jpg', 'zip'],
        blockedTypes: ['exe', 'bat', 'js', 'cmd', 'scr']
    });

    useEffect(() => {
        const loadPolicies = async () => {
            try {
                const data = await adminService.getPolicies();
                if (data.storage) setStoragePolicies(data.storage);
                if (data.transmission) setTransmissionPolicies(data.transmission);
                if (data.attachments) setAttachmentPolicies(data.attachments);
            } catch (err) {
                console.warn("Failed to fetch live policies. Using defaults.");
            } finally {
                setFetching(false);
            }
        };
        loadPolicies();
    }, []);

    const handleSave = async (category, policies) => {
        setLoading(true);
        setAppError('');
        try {
            await adminService.updatePolicies(category, policies);
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            const msg = err.response?.data?.message || err.message || "Unknown error";
            setAppError(`Failed to save ${category} policies: ${msg}`);
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveType = (field, type) => {
        setAttachmentPolicies(prev => ({
            ...prev,
            [field]: prev[field].filter(t => t !== type)
        }));
    };

    const handleAddType = (field) => {
        const type = window.prompt(`Enter extension to ${field === 'allowedTypes' ? 'allow' : 'block'} (e.g. rar, dmg):`);
        if (type) {
            const clean = type.trim().toLowerCase().replace(/^\./, '');
            if (clean && !attachmentPolicies[field].includes(clean)) {
                setAttachmentPolicies(prev => ({
                    ...prev,
                    [field]: [...prev[field], clean]
                }));
            }
        }
    };

    return (
        <div className="max-w-6xl mx-auto space-y-8 animate-fade-in pb-20">
            {/* Header Section */}
            <header className="flex justify-between items-end">
                <div>
                    <h1 className="text-3xl font-extrabold tracking-tight underline decoration-[#4f46e5]/30 decoration-4 underline-offset-8 uppercase tracking-wider">
                        Global Policies
                    </h1>
                    <p className="text-slate-500 mt-2 font-medium">
                        Define global guardrails for storage, transmission, and security.
                    </p>
                </div>

                <div className="flex flex-col items-end space-y-4">
                    <div className="flex items-center space-x-3">
                        <label className="flex items-center cursor-pointer space-x-3 bg-white dark:bg-slate-900 border border-[rgb(var(--border))] px-4 py-2.5 rounded-2xl transition-all hover:border-[#4f46e5]/30 group">
                            <div className="relative">
                                <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={forcePropagate}
                                    onChange={() => setForcePropagate(!forcePropagate)}
                                />
                                <div className="w-9 h-5 bg-slate-200 dark:bg-slate-800 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#4f46e5]"></div>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[9px] font-black uppercase tracking-widest text-slate-900 dark:text-white">Force Propagation</span>
                                <span className="text-[7px] text-slate-400 font-bold uppercase">Override Local Sets</span>
                            </div>
                        </label>

                        <button
                            onClick={() => handleSave('all', { storage: storagePolicies, transmission: transmissionPolicies, attachments: attachmentPolicies, force: forcePropagate })}
                            disabled={loading}
                            className="premium-button-primary px-8 py-3.5 flex items-center gap-2"
                        >
                            {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save size={18} />}
                            <span className="text-[10px] font-black uppercase tracking-widest">{loading ? 'Syncing...' : 'Sync Cluster Policies'}</span>
                        </button>
                    </div>
                    {success && (
                        <div className="flex items-center space-x-2 text-emerald-500 font-bold text-[10px] uppercase tracking-widest bg-emerald-500/5 px-4 py-2 rounded-xl animate-fade-in border border-emerald-500/20">
                            <CheckCircle2 size={12} />
                            <span>Cluster Synced OK</span>
                        </div>
                    )}
                    {error && (
                        <div className="flex items-center space-x-2 text-rose-500 font-bold text-[10px] uppercase tracking-widest bg-rose-500/5 px-4 py-2 rounded-xl animate-fade-in border border-rose-500/20">
                            <AlertCircle size={12} />
                            <span>Sync Logic Error</span>
                        </div>
                    )}
                </div>
            </header>

            {fetching ? (
                <div className="flex flex-col items-center justify-center py-40 space-y-6">
                    <div className="w-16 h-16 border-4 border-[#4169E1]/20 border-t-[#4169E1] rounded-full animate-spin shadow-2xl shadow-[#4169E1]/20" />
                    <div className="flex flex-col items-center">
                        <p className="text-[#4169E1] font-black uppercase tracking-[0.3em] text-sm">Synchronizing Architecture</p>
                        <p className="text-slate-400 text-[10px] font-bold uppercase mt-2">Connecting to Policy Engine...</p>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* 1. Mailbox Storage Policies */}
                    <PolicySection
                        title="Mailbox Storage Policies"
                        icon={Database}
                        description="Account-level storage architecture and retention."
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <InputField
                                label="Default Quota"
                                value={storagePolicies.defaultQuota}
                                onChange={(val) => setStoragePolicies({ ...storagePolicies, defaultQuota: val })}
                                helper="Provisioned on new mailbox creation."
                            />
                            <InputField
                                label="Maximum Quota"
                                value={storagePolicies.maxMailboxSize}
                                onChange={(val) => setStoragePolicies({ ...storagePolicies, maxMailboxSize: val })}
                                helper="Hard limit for enterprise accounts."
                            />
                            <InputField
                                label="Warning Threshold"
                                value={storagePolicies.warningThreshold}
                                onChange={(val) => setStoragePolicies({ ...storagePolicies, warningThreshold: val })}
                                helper="Trigger notification at this usage %"
                            />
                            <InputField
                                label="Attachment Size Limit"
                                value={storagePolicies.attachmentLimit}
                                onChange={(val) => setStoragePolicies({ ...storagePolicies, attachmentLimit: val })}
                                helper="Global max for single file."
                            />
                            <InputField
                                label="Auto Archive"
                                value={storagePolicies.archivePolicy}
                                onChange={(val) => setStoragePolicies({ ...storagePolicies, archivePolicy: val })}
                                helper="Move to cold storage after X days."
                            />
                            <InputField
                                label="Trash Retention"
                                value={storagePolicies.trashRetention}
                                onChange={(val) => setStoragePolicies({ ...storagePolicies, trashRetention: val })}
                                helper="Purge deleted items after X days."
                            />
                        </div>
                    </PolicySection>

                    {/* 2. Message Transmission Policies */}
                    <PolicySection
                        title="Transmission Policies"
                        icon={Send}
                        description="MTA throughput and delivery constraints."
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <InputField
                                label="Max Recipients"
                                type="number"
                                value={transmissionPolicies.maxRecipients}
                                onChange={(val) => setTransmissionPolicies({ ...transmissionPolicies, maxRecipients: val })}
                                helper="Per single outgoing message."
                            />
                            <InputField
                                label="Emails Per Hour"
                                type="number"
                                value={transmissionPolicies.emailsPerHour}
                                onChange={(val) => setTransmissionPolicies({ ...transmissionPolicies, emailsPerHour: val })}
                                helper="Throttling limit per account."
                            />
                            <InputField
                                label="Emails Per Day"
                                type="number"
                                value={transmissionPolicies.emailsPerDay}
                                onChange={(val) => setTransmissionPolicies({ ...transmissionPolicies, emailsPerDay: val })}
                                helper="Daily volume maximum."
                            />
                            <InputField
                                label="Max Attachment Count"
                                type="number"
                                value={transmissionPolicies.attachmentCount}
                                onChange={(val) => setTransmissionPolicies({ ...transmissionPolicies, attachmentCount: val })}
                                helper="Total files per message."
                            />
                            <InputField
                                label="SMTP Connections"
                                type="number"
                                value={transmissionPolicies.smtpLimit}
                                onChange={(val) => setTransmissionPolicies({ ...transmissionPolicies, smtpLimit: val })}
                                helper="Concurrent connection limit."
                            />
                            <InputField
                                label="Retry Attempts"
                                type="number"
                                value={transmissionPolicies.retryAttempts}
                                onChange={(val) => setTransmissionPolicies({ ...transmissionPolicies, retryAttempts: val })}
                                helper="Before permanent delivery failure."
                            />
                        </div>
                    </PolicySection>

                    {/* 3. File Attachment Policies */}
                    <div className="lg:col-span-2">
                        <PolicySection
                            title="File Attachment Policies"
                            icon={Paperclip}
                            description="Security guardrails for incoming and outgoing payloads."
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                                <div className="space-y-4">
                                    <div className="flex items-center space-x-2 text-green-600 font-bold text-xs uppercase tracking-widest">
                                        <FileType size={14} />
                                        <span>Whitelist (Allowed)</span>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {attachmentPolicies.allowedTypes.map((type) => (
                                            <div key={type} className="bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400 px-3 py-1.5 rounded-lg text-xs font-bold border border-green-100 dark:border-green-500/20 flex items-center space-x-2">
                                                <span>.{type}</span>
                                                <button onClick={() => handleRemoveType('allowedTypes', type)} className="hover:text-red-500 transition-colors">
                                                    <X size={12} strokeWidth={3} />
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            onClick={() => handleAddType('allowedTypes')}
                                            className="bg-slate-100 dark:bg-slate-800 text-slate-500 px-3 py-1.5 rounded-lg text-xs font-bold border border-dashed border-slate-300 dark:border-slate-700 hover:border-[#4169E1] hover:text-[#4169E1] transition-all"
                                        >
                                            + Add Extension
                                        </button>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="flex items-center space-x-2 text-red-600 font-bold text-xs uppercase tracking-widest">
                                        <XCircle size={14} />
                                        <span>Blacklist (Blocked)</span>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {attachmentPolicies.blockedTypes.map((type) => (
                                            <div key={type} className="bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 px-3 py-1.5 rounded-lg text-xs font-bold border border-red-100 dark:border-red-500/20 flex items-center space-x-2">
                                                <span>.{type}</span>
                                                <button onClick={() => handleRemoveType('blockedTypes', type)} className="hover:text-red-500 transition-colors">
                                                    <X size={12} strokeWidth={3} />
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            onClick={() => handleAddType('blockedTypes')}
                                            className="bg-slate-100 dark:bg-slate-800 text-slate-500 px-3 py-1.5 rounded-lg text-xs font-bold border border-dashed border-slate-300 dark:border-slate-700 hover:border-[#4169E1] hover:text-[#4169E1] transition-all"
                                        >
                                            + Add Extension
                                        </button>
                                    </div>
                                    <p className="text-[10px] text-slate-400 font-medium bg-slate-50 dark:bg-slate-900 p-3 rounded-xl border border-slate-100 dark:border-slate-800 flex items-start space-x-2">
                                        <AlertCircle size={12} className="shrink-0 mt-0.5" />
                                        <span>Blocked types are automatically stripped from both inbound and outbound traffic at the MTA edge.</span>
                                    </p>
                                </div>
                            </div>
                        </PolicySection>
                    </div>
                </div>
            )}

            {/* Info Footer */}
            <div className="bg-[#4169E1]/5 border border-[#4169E1]/10 rounded-3xl p-6 flex items-start space-x-4">
                <Info className="text-[#4169E1] shrink-0" size={20} />
                <div className="space-y-1">
                    <p className="text-sm font-bold text-[#4169E1]">Global Policy Propagation</p>
                    <p className="text-xs text-[#4169E1]/70 leading-relaxed font-medium">
                        Changes to storage and transmission policies are propagated immediately across all active MTA nodes.
                        Existing mailboxes will inherit these policies upon their next sync cycle (approx. 5-10 minutes).
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PolicyManagement;
